import { useEffect, useRef } from "react";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { tokenIsDue, useDolfinAuth } from "@/components/DolfinProvider";

type EInvoiceStateChanged = {
  invoiceId: string;
  creditNoteId?: string | null;
  documentKind: string;
  toState: string;
};
type InvoiceEmailSent = { invoiceId: string; outcome: string };
type InvoiceStateChanged = { invoiceId: string; fromState: string; toState: string };

/**
 * Live delivery updates instead of polling: an SDI verdict can take hours. Same connection rules as
 * `useBillStateChange` (`withCredentials` false); without `hubBaseUrl` it is a no-op.
 */
export function useInvoiceDeliveryChange(
  invoiceId: string | undefined,
  enabled: boolean,
  onChange: () => void,
  options: { hubBaseUrl?: string } = {},
) {
  const { auth, organisationId, refresh } = useDolfinAuth();
  const hasToken = !!auth?.token;
  // Read at (re)connect time rather than captured, so a token refresh doesn't tear the connection down.
  const tokenRef = useRef(auth?.token);
  tokenRef.current = auth?.token;
  const hubBaseUrl = options.hubBaseUrl;
  // The latest callback, so a re-render with a new closure does not tear down the connection.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!enabled || !invoiceId || !hubBaseUrl || !hasToken || !organisationId) return;

    const hubUrl = `${hubBaseUrl}/hubs/invoices?organisationId=${encodeURIComponent(organisationId)}`;
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const current = tokenRef.current;
          if (current && !tokenIsDue(current)) return current;
          return (await refresh())?.token ?? current ?? "";
        },
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const eInvoiceHandler = (msg: EInvoiceStateChanged) => {
      if (msg.invoiceId === invoiceId && msg.documentKind === "Invoice") onChangeRef.current();
    };
    const emailHandler = (msg: InvoiceEmailSent) => {
      if (msg.invoiceId === invoiceId) onChangeRef.current();
    };
    const stateHandler = (msg: InvoiceStateChanged) => {
      if (msg.invoiceId === invoiceId) onChangeRef.current();
    };
    connection.on("eInvoiceStateChanged", eInvoiceHandler);
    connection.on("invoiceEmailSent", emailHandler);
    connection.on("invoiceStateChanged", stateHandler);

    // Cleanup stops a connection that may still be negotiating (the page re-renders as the invoice loads);
    // that abort is ours, not a failure worth logging.
    let stopped = false;
    connection.start().catch((err) => {
      if (!stopped) console.error("SignalR connect failed", err);
    });

    return () => {
      stopped = true;
      connection.off("eInvoiceStateChanged", eInvoiceHandler);
      connection.off("invoiceEmailSent", emailHandler);
      connection.off("invoiceStateChanged", stateHandler);
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [enabled, invoiceId, hubBaseUrl, hasToken, organisationId, refresh]);
}
